import { Button, Dialog } from "@cloudflare/kumo";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { kvTargetKey } from "../../api/kv/model.ts";
import { AddKVForm } from "./add-form.tsx";
import { KVSearchForm } from "./search-form.tsx";
import { KVTable } from "./table.tsx";
import { useKVBrowse } from "./react.tsx";

interface KVBrowseSurfaceProps { readonly workspaceId: string; readonly bindingName: string; readonly namespace: string }
export function KVBrowseSurface({ workspaceId, bindingName, namespace }: KVBrowseSurfaceProps) {
  const target = { workspaceId, bindingName, namespace }, viewScope = kvTargetKey(target);
  const { snapshot, dispatch } = useKVBrowse(target);
  const { entries, cursor, prefix, error, loading, loadingMore, mutating, savingAdd, savingEdit, deleting, overwriting, deleteTarget, overwriteConfirm, clearAddForm, clearEditForm } = snapshot;
  const handleLoadMore = () => dispatch({ _tag: "LoadMore" });
  const handleSearch = (prefix: string) => dispatch({ _tag: "Search", prefix });
  const handleAdd = (key: string, value: string) => dispatch({ _tag: "Add", key, value });
  const handleEditSave = (originalKey: string, key: string, value: string) => dispatch({ _tag: "Edit", originalKey, key, value });
  const handleConfirmDelete = () => dispatch({ _tag: "ConfirmDelete" });
  const closeDeleteDialog = () => dispatch({ _tag: "CloseDelete" });
  const handleConfirmOverwrite = () => dispatch({ _tag: "ConfirmOverwrite" });
  const closeOverwriteDialog = () => dispatch({ _tag: "CloseOverwrite" });
  return (
    <div className="h-full min-h-0 overflow-auto bg-kumo-base">
      <div className="px-6 py-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-kumo-default">
              {bindingName}
            </h2>
            <p className="truncate text-xs text-kumo-subtle">
              KV namespace <code>{namespace}</code>
            </p>
          </div>
          <Button
            variant="secondary"
            shape="square"
            aria-label="Refresh entries"
            title="Refresh entries"
            disabled={loading}
            onClick={() => dispatch({ _tag: "Refresh" })}
          >
            <ArrowsClockwiseIcon
              size={16}
              className={loading ? "animate-spin" : ""}
            />
          </Button>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-kumo-danger/20 bg-kumo-danger/8 p-4 text-kumo-danger">
            {error}
          </div>
        )}

        <KVSearchForm
          key={`${viewScope}:search`}
          onSearch={handleSearch}
          disabled={loading}
        />

        <hr className="my-4 border-kumo-fill" />

        <AddKVForm
          key={`${viewScope}:add`}
          onAdd={handleAdd}
          clearSignal={clearAddForm}
          disabled={mutating}
          saving={savingAdd}
        />

        {loading ? (
          <div className="p-12 text-center text-kumo-subtle">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center space-y-2 p-12 text-center text-kumo-subtle">
            {prefix ? (
              <p className="text-sm font-light">{`No keys found matching prefix "${prefix}".`}</p>
            ) : (
              <>
                <h2 className="text-2xl font-medium">
                  No keys in this namespace
                </h2>
                <p className="text-sm font-light">
                  Add an entry using the form above.
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="rounded-lg">
              <KVTable
                key={`${viewScope}:table`}
                entries={entries}
                onSave={handleEditSave}
                disabled={mutating}
                saving={savingEdit}
                clearSignal={clearEditForm}
                onDelete={(key: string) => dispatch({ _tag: "AskDelete", key })}
              />
            </div>
            {cursor && (
              <div className="py-4 text-center">
                <Button
                  variant="secondary"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  loading={loadingMore}
                >
                  {loadingMore ? "Loading..." : "Load More"}
                </Button>
              </div>
            )}
          </>
        )}

        <Dialog.Root
          open={deleteTarget !== null}
          onOpenChange={(open: boolean) => !open && closeDeleteDialog()}
        >
          <Dialog className="p-6">
            <Dialog.Title className="mb-4 text-lg font-semibold">
              Delete key?
            </Dialog.Title>
            <Dialog.Description className="mb-2 text-kumo-subtle">
              Are you sure you want to delete &ldquo;{deleteTarget}&rdquo;?
              This cannot be undone.
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={closeDeleteDialog}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={deleting}
                loading={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </Dialog>
        </Dialog.Root>

        <Dialog.Root
          open={overwriteConfirm !== null}
          onOpenChange={(open: boolean) => !open && closeOverwriteDialog()}
        >
          <Dialog className="p-6">
            <Dialog.Title className="mb-4 text-lg font-semibold">
              Overwrite key?
            </Dialog.Title>
            <Dialog.Description className="mb-2 text-kumo-subtle">
              Key &ldquo;{overwriteConfirm?.key}&rdquo; already exists. Do you
              want to overwrite its value?
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={closeOverwriteDialog}
                disabled={overwriting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmOverwrite}
                disabled={overwriting}
                loading={overwriting}
              >
                {overwriting ? "Overwriting..." : "Overwrite"}
              </Button>
            </div>
          </Dialog>
        </Dialog.Root>
      </div>
    </div>
  );
}
